import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  MRT_ColumnFiltersState,
} from 'material-react-table';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Box, Button, lighten, Typography, Select, MenuItem, Checkbox, ListItemText, Chip, OutlinedInput } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useNavigate, useParams } from 'react-router-dom';
import PulseLoader from '@/components/PulseLoader';
import MediaQuery from 'react-responsive';
import { Modal, Button as BootstrapButton } from 'react-bootstrap';
import { authService } from '../../../utils/authService';
import { fetchCoursesByTrack } from "../../../utils/courseUtils";
import { fetchBatchesByCourseSingle } from "../../../utils/batchUtils";
import { fetchAllTracksForCourses } from "../../../utils/trackUtils";
import { getViewSessionsPath } from "./sessionUtils/sessionUtils";

// Type definitions
interface Student {
  stuId: string;
  stuname: string;
  gender: string;
  phonenumber: string;
  branch: string;
  collegeName: string;
  email: string;
  key?: string;
}

// Removed College and Branch interfaces - no longer needed

interface Track {
  track_id: string;
  track_name: string;
}

interface Course {
  course_id: string;
  course_name: string;
}

interface Batch {
  batch_id: string;
  batch_name: string;
}

interface ProjectOption {
  batch_project_id: number | string;
  batch_project_name: string;
  project_id?: number | string;
  project_name?: string;
}

// Removed ApiResponse interface - no longer needed

interface CheckboxTableProps {
  Sessionid?: number;
}

// Removed fetchData function - now extracting colleges and branches from student data

const StudentSelecTable: React.FC<CheckboxTableProps> = ({ Sessionid }) => {
  const { sessionName } = useParams<{ sessionName: string }>();
  
  // Get URL parameters for session ID, track, course and batch
  const urlParams = new URLSearchParams(window.location.search);
  const sessionIdFromUrl = urlParams.get('sessionId');
  const parseMultiParam = (value: string | null): string[] =>
    value ? value.split(',').map((item) => decodeURIComponent(item).trim()).filter(Boolean) : [];
  const preselectedTrack = parseMultiParam(urlParams.get('track'));
  const preselectedCourse = parseMultiParam(urlParams.get('course'));
  const preselectedBatch = parseMultiParam(urlParams.get('batch'));
  const preselectedSubject = parseMultiParam(urlParams.get('subject'));
  const preselectedProject = parseMultiParam(urlParams.get('project'));
  
  const [session_id, setSession_id] = useState<number>(
    sessionIdFromUrl ? parseInt(sessionIdFromUrl) : (Sessionid || 0)
  );
  
  const [collegeOptions, setCollegeOptions] = useState<{ value: string; label: string }[]>([]);
  const [branchOptions, setBranchOptions] = useState<{ value: string; label: string }[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [isAdding, setIsAdding] = useState<boolean>(false); 
  const [showModal, setShowModal] = useState<boolean>(false); 
  const [successfulMessage, setSuccessfulMessage] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const navigate = useNavigate();

  // Filter states
  const [tracks, setTracks] = useState<Track[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<string[]>(preselectedTrack);
  const [selectedCourse, setSelectedCourse] = useState<string[]>(preselectedCourse);
  const [selectedBatch, setSelectedBatch] = useState<string[]>(preselectedBatch);
  const [selectedSubject, setSelectedSubject] = useState<string[]>(preselectedSubject);
  const [selectedProject, setSelectedProject] = useState<string[]>(preselectedProject);
  const [loadingTracks, setLoadingTracks] = useState<boolean>(false);
  const [loadingCourses, setLoadingCourses] = useState<boolean>(false);
  const [loadingBatches, setLoadingBatches] = useState<boolean>(false);
  const [loadingSubjects, setLoadingSubjects] = useState<boolean>(false);
  const [loadingProjects, setLoadingProjects] = useState<boolean>(false);
  const [loadingStudents, setLoadingStudents] = useState<boolean>(false);
  
  // State to track if dropdowns should be disabled (when coming from CreateSession)
  const isFromCreateSession = preselectedTrack.length > 0 || preselectedCourse.length > 0 || preselectedBatch.length > 0;
  const initialTrackPrefillHandledRef = useRef(false);
  const initialCoursePrefillHandledRef = useRef(false);
  const initialProjectPrefillHandledRef = useRef(false);
  const initialSubjectPrefillHandledRef = useRef(false);
  
  


  useEffect(() => {
    // Check if user is trying to access admin route without admin permission
    const currentPath = window.location.pathname;
    const isAdminRoute = currentPath.startsWith('/admin');
    const hasAdminPermission = sessionStorage.getItem("Admin");
    
    if (isAdminRoute && !hasAdminPermission) {
      // Redirect to trainer equivalent route
      const trainerPath = currentPath.replace('/admin', '/trainer');
      navigate(trainerPath);
      return;
    }
    
  const fetchInitialData = async (): Promise<void> => {
      // Initial data loading completed
    };
    
    fetchInitialData();
    fetchTracks();
  }, []);

  // Reset courses, batches when track changes (but retain selected students)
  useEffect(() => {
    if (selectedTrack.length > 0) {
      if (isFromCreateSession && !initialTrackPrefillHandledRef.current) {
        initialTrackPrefillHandledRef.current = true;
        fetchCourses(selectedTrack);
        return;
      }
      setCourses([]);
      setBatches([]);
      setSubjects([]);
      setProjects([]);
      setStudents([]);
      setSelectedCourse([]);
      setSelectedBatch([]);
      setSelectedSubject([]);
      setSelectedProject([]);
      setCollegeOptions([]); // Clear college options
      setBranchOptions([]); // Clear branch options
      // Don't clear selectedRows - retain selections when changing track/course/batch
      fetchCourses(selectedTrack);
    } else {
      setCourses([]);
      setBatches([]);
      setSubjects([]);
      setProjects([]);
      setStudents([]);
      setSelectedCourse([]);
      setSelectedBatch([]);
      setSelectedSubject([]);
      setSelectedProject([]);
      setCollegeOptions([]); // Clear college options
      setBranchOptions([]); // Clear branch options
      // Don't clear selectedRows - retain selections when changing track/course/batch
    }
  }, [selectedTrack]);

  // Reset batches when course changes (but retain selected students)
  useEffect(() => {
    if (selectedCourse.length > 0) {
      if (isFromCreateSession && !initialCoursePrefillHandledRef.current) {
        initialCoursePrefillHandledRef.current = true;
        fetchBatches(selectedCourse);
        return;
      }
      setBatches([]);
      setSubjects([]);
      setProjects([]);
      setStudents([]);
      setSelectedBatch([]);
      setSelectedSubject([]);
      setSelectedProject([]);
      setCollegeOptions([]); // Clear college options
      setBranchOptions([]); // Clear branch options
      // Don't clear selectedRows - retain selections when changing course/batch
      fetchBatches(selectedCourse);
    } else {
      setBatches([]);
      setSubjects([]);
      setProjects([]);
      setStudents([]);
      setSelectedBatch([]);
      setSelectedSubject([]);
      setSelectedProject([]);
      setCollegeOptions([]); // Clear college options
      setBranchOptions([]); // Clear branch options
      // Don't clear selectedRows - retain selections when changing course/batch
    }
  }, [selectedCourse]);

  // Update session_id when Sessionid prop or URL parameter changes
  useEffect(() => {
    if (sessionIdFromUrl) {
      setSession_id(parseInt(sessionIdFromUrl));
    } else if (Sessionid && Sessionid !== 0) {
      setSession_id(Sessionid);
    }
  }, [Sessionid, sessionIdFromUrl]);

  // preselected values are loaded directly from URL arrays at state init

  // Update selectAll state when students data changes
  useEffect(() => {
    if (students.length > 0) {
      const currentStudentIds = students.map(student => student.stuId);
      const currentSelections = selectedRows.filter(id => currentStudentIds.includes(id));
      setSelectAll(currentSelections.length === currentStudentIds.length);
    } else {
      setSelectAll(false);
    }
  }, [students, selectedRows]);

  // Fetch students when batch is selected (both manual and from CreateSession)
  useEffect(() => {
    if (selectedCourse.length > 0 && selectedBatch.length > 0) {
      fetchStudentsByFilters(selectedCourse, selectedBatch, selectedSubject, selectedProject);
    }
  }, [selectedCourse, selectedBatch, selectedSubject, selectedProject]);

  useEffect(() => {
    if (selectedCourse.length > 0 && selectedBatch.length > 0) {
      if (isFromCreateSession && !initialSubjectPrefillHandledRef.current) {
        initialSubjectPrefillHandledRef.current = true;
        fetchSubjects(selectedCourse, selectedBatch);
        if (!initialProjectPrefillHandledRef.current) {
          initialProjectPrefillHandledRef.current = true;
          fetchProjects(selectedCourse, selectedBatch);
        }
        return;
      }
      setSubjects([]);
      setSelectedSubject([]);
      fetchSubjects(selectedCourse, selectedBatch);
      fetchProjects(selectedCourse, selectedBatch);
    } else {
      setSubjects([]);
      setSelectedSubject([]);
      setProjects([]);
      setSelectedProject([]);
    }
  }, [selectedCourse, selectedBatch, courses, batches]);

  // API functions
  const fetchTracks = async (): Promise<void> => {
    setLoadingTracks(true);
    try {
      const data = await fetchAllTracksForCourses();
      setTracks(data);
    } catch (error) {
      console.error('Error fetching tracks:', error);
    } finally {
      setLoadingTracks(false);
    }
  };

  const fetchCourses = async (trackIds: string[]): Promise<void> => {
    setLoadingCourses(true);
    try {
      const responses = await Promise.all(trackIds.map((trackId) => fetchCoursesByTrack(trackId)));
      const merged = responses.flat();
      const deduped = merged.filter(
        (course, index, arr) => arr.findIndex((item) => item.course_id === course.course_id) === index
      );
      setCourses(deduped);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoadingCourses(false);
    }
  };


  const fetchBatches = async (courseIds: string[]): Promise<void> => {
    setLoadingBatches(true);
    try {
      const responses = await Promise.all(courseIds.map((courseId) => fetchBatchesByCourseSingle(courseId)));
      const merged = responses.flat();
      const deduped = merged.filter(
        (batch, index, arr) => arr.findIndex((item) => item.batch_id === batch.batch_id) === index
      );
      setBatches(deduped);
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoadingBatches(false);
    }
  };

  const fetchSubjects = async (courseIds: string[], batchIds: string[]): Promise<void> => {
    setLoadingSubjects(true);
    try {
      const responses = await Promise.all(
        courseIds.flatMap((courseId) =>
          batchIds.map((batchId) => authService.get(`admin/batch/get_subject_for_batch/${courseId}/${batchId}/`))
        )
      );
      const merged = responses.flatMap((response) => (response.status === 200 ? response?.data?.subjects || [] : []));
      const deduped = merged.filter((subject: string, index: number, arr: string[]) => arr.indexOf(subject) === index);
      setSubjects(deduped);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const fetchProjects = async (courseIds: string[], batchIds: string[]): Promise<void> => {
    setLoadingProjects(true);
    try {
      const response = await authService.get('filters/analysis/');
      const projectsByCourseBatch = response?.data?.projects || {};
      const courseIdToName = Object.fromEntries(
        courses.map((course) => [String(course.course_id), String(course.course_name)])
      );
      const batchIdToName = Object.fromEntries(
        batches.map((batch) => [String(batch.batch_id), String(batch.batch_name)])
      );
      const merged = courseIds.flatMap((courseId) =>
        batchIds.flatMap((batchId) => {
          const courseKey = courseIdToName[String(courseId)] || String(courseId);
          const batchKey = batchIdToName[String(batchId)] || String(batchId);
          return (
            projectsByCourseBatch?.[courseKey]?.[batchKey] ||
            projectsByCourseBatch?.[String(courseId)]?.[String(batchId)] ||
            []
          );
        })
      );
      const deduped = merged.filter(
        (project: ProjectOption, index: number, arr: ProjectOption[]) =>
          arr.findIndex(
            (item) => String(item.batch_project_id) === String(project.batch_project_id)
          ) === index
      );
      setProjects(deduped);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };


  const fetchStudentsByFilters = async (
    courseIds: string[],
    batchIds: string[],
    subjectIds: string[],
    projectIds: string[]
  ): Promise<void> => {
    setLoadingStudents(true);
    try {
      const responses = await Promise.all(
        courseIds.flatMap((courseId) =>
          batchIds.map((batchId) => {
            let url = `/student/getStudents/session-filtered/?course_id=${courseId}&batch_id=${batchId}`;
            if (subjectIds.length > 0) {
              url += `&subject_id=${encodeURIComponent(subjectIds.join(','))}`;
            }
            if (projectIds.length > 0) {
              url += `&project_ids=${encodeURIComponent(projectIds.join(','))}`;
            }
            return authService.get(url);
          })
        )
      );
      const mergedStudents: Student[] = [];
      responses.forEach((response) => {
        if (response.status === 200) {
          if (Array.isArray(response.data)) {
            mergedStudents.push(...response.data);
          } else if (response.data.students && Array.isArray(response.data.students)) {
            mergedStudents.push(...response.data.students);
          }
        }
      });
      const uniqueStudents = mergedStudents.filter(
        (student, index, arr) => arr.findIndex((item) => item.stuId === student.stuId) === index
      );
      const studentsData = uniqueStudents.map((student: Student) => ({ ...student, key: student.stuId }));
        
        setStudents(studentsData);
        
        // Extract unique colleges and branches from student data
        if (studentsData.length > 0) {
          const uniqueColleges = Array.from(new Set(studentsData.map((student: Student) => student.collegeName))) as string[];
          const uniqueBranches = Array.from(new Set(studentsData.map((student: Student) => student.branch))) as string[];
          
          setCollegeOptions(uniqueColleges.map(college => ({ value: college, label: college })));
          setBranchOptions(uniqueBranches.map(branch => ({ value: branch, label: branch })));
        } else {
          setCollegeOptions([]);
          setBranchOptions([]);
        }
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleMultiSelectChange = (setter: (value: string[]) => void) => (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setter(typeof value === 'string' ? value.split(',') : value);
  };

  const renderSelectedChips = (
    selected: string[],
    labels: Record<string, string>,
    onRemove?: (value: string) => void,
    maxVisible: number = selected.length
  ) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
      {selected.slice(0, maxVisible).map((value) => (
        <Chip
          key={value}
          label={labels[value] || value}
          size="small"
          onDelete={
            onRemove
              ? (event) => {
                  event.stopPropagation();
                  onRemove(value);
                }
              : undefined
          }
          deleteIcon={
            onRemove ? (
              <span
                onMouseDown={(event) => event.stopPropagation()}
                style={{ fontSize: '15px', lineHeight: 1, fontWeight: 700 }}
              >
                x
              </span>
            ) : undefined
          }
          sx={{ height: 20 }}
        />
      ))}
      {selected.length > maxVisible && (
        <Chip
          label={`+${selected.length - maxVisible} projects`}
          size="small"
          sx={{ height: 20, pointerEvents: 'none' }}
        />
      )}
    </Box>
  );

  const dropdownMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 280,
        marginTop: 4,
        zIndex: 1500,
      },
    },
    transformOrigin: { horizontal: 'left' as const, vertical: 'top' as const },
    anchorOrigin: { horizontal: 'left' as const, vertical: 'bottom' as const },
  };



  const handleSelectAllChange = (): void => {
    if (selectAll) {
      // Deselect all current students, but keep selections from other courses/batches
      const currentStudentIds = students.map(student => student.stuId);
      setSelectedRows(selectedRows.filter(id => !currentStudentIds.includes(id)));
    } else {
      // Select all current students, keeping existing selections from other courses/batches
      const currentStudentIds = students.map(student => student.stuId);
      const newSelections = Array.from(new Set([...selectedRows, ...currentStudentIds]));
      setSelectedRows(newSelections);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectRowChange = (studentId: string): void => {
    const newSelectedRows = selectedRows.includes(studentId)
      ? selectedRows.filter(id => id !== studentId)
      : [...selectedRows, studentId];
    setSelectedRows(newSelectedRows);
    
    // Update select all checkbox based on current students only
    const currentStudentIds = students.map(student => student.stuId);
    const currentSelections = newSelectedRows.filter(id => currentStudentIds.includes(id));
    setSelectAll(currentSelections.length === currentStudentIds.length && currentStudentIds.length > 0);
  };

  const handleLogSelected = async (): Promise<void> => {
    if (session_id === 0) {
      console.error('Session ID is 0, cannot add students');
      return;
    }
    
    const selectedStudents = students.filter(student => selectedRows.includes(student.stuId));
    const dataToSend = {
      session_id : String(session_id), 
      Students: selectedStudents,
    };
    setIsAdding(true);
    try {
      const response = await authService.post('/session/add-students/', dataToSend);
      setSuccessfulMessage(response.data.message);
      setShowModal(true); 
    } catch (error) {
      console.error('Error sending data:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const columns = useMemo(() => [
    {
      accessorFn: (row: Student) => row.stuname,
      id: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'collegeName',
      header: 'College',
      filterVariant: 'multi-select' as const,
      filterSelectOptions: collegeOptions.map(option => option.value),
      filterFn: (row: any, _columnIds: any, filterValue: string[]) => {
        if (!filterValue.length) return true;
        return filterValue.includes(row.original.collegeName);
      },
    },
    {
      accessorKey: 'branch',
      header: 'Branch',
      filterVariant: 'multi-select' as const,
      filterSelectOptions: branchOptions.map(option => option.value),
      filterFn: (row: any, _columnIds: any, filterValue: string[]) => {
        if (!filterValue.length) return true;
        return filterValue.includes(row.original.branch);
      },
    },
    {
      accessorFn: (row: Student) => row.phonenumber,
      id: 'phone',
      header: 'Phone Number',
    },
    {
      accessorFn: (row: Student) => row.email,
      id: 'email',
      header: 'Email',
    },
  ], [collegeOptions, branchOptions]);

  const table = useMaterialReactTable({
    columns,
    data: students,

    enableRowSelection: true,
    getRowId: (row: Student) => row.stuId,
    state: {
      rowSelection: selectedRows.reduce((acc, id) => ({ ...acc, [id]: true }), {}),
    },
    onRowSelectionChange: (updaterOrValue) => {
      const currentSelection = selectedRows.reduce((acc, id) => ({ ...acc, [id]: true }), {});
      const newSelection = typeof updaterOrValue === 'function' 
        ? updaterOrValue(currentSelection) 
        : updaterOrValue;
      
      // Get current students' IDs
      const currentStudentIds = students.map(student => student.stuId);
      
      // Remove current students from selectedRows, then add new selections
      const otherSelections = selectedRows.filter(id => !currentStudentIds.includes(id));
      const newCurrentSelections = Object.keys(newSelection).filter(id => currentStudentIds.includes(id));
      
      const finalSelections = [...otherSelections, ...newCurrentSelections];
      
      setSelectedRows(finalSelections);
      
      // Update selectAll state for current students
      const currentSelections = newCurrentSelections.length;
      setSelectAll(currentSelections === currentStudentIds.length && currentStudentIds.length > 0);
    },
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableColumnResizing: true,
    enableHiding: false,
    enableDensityToggle: true,
    enableFullScreenToggle: true,
    initialState: {
      density: 'compact' as const,
      pagination: {
        pageSize: 20,
        pageIndex: 0,
      },
      columnPinning: {
        left: ['mrt-row-select'],
      },
      showColumnFilters: false,
    },
    muiTablePaperProps: {
      sx: {
        borderRadius: "10px",
      },
    },
    positionToolbarAlertBanner: 'none' as const,
    muiTableContainerProps:{
      sx: {
        height: "100%",  
        overflow: "auto",
      },   
    },

    renderTopToolbar: ({ table }) => {
      const handleLogSelected = async (): Promise<void> => {
        if (session_id === 0) {
          console.error('Session ID is 0, cannot add students');
          return;
        }
        
        const selectedStudents = students.filter(student => selectedRows.includes(student.stuId));
        const dataToSend = {
          session_id : String(session_id), 
          Students: selectedStudents,
        };
        
        setIsAdding(true);
        try {
          const response = await authService.post('/session/add-students/', dataToSend);
          setSuccessfulMessage(response.data.message);
          setShowModal(true); 
        } catch (error) {
          console.error('Error sending data:', error);
        } finally {
          setIsAdding(false);
        }
      };

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'inherit',
            m: 0,
            p: 0,
          }}
        >
          {/* Filters Section - matching ViewSessions.tsx style */}
          <Box sx={{ 
            display: 'flex',
            gap: '0.75rem', 
            alignItems: 'flex-start',
            flexWrap: 'wrap', 
            p: 1.5, 
            backgroundColor: '#f8f9fa',
            borderRadius: 1,
            mb: 2,
            overflow: 'visible'
          }}>
            <Box sx={{ minWidth: '180px' }}>
              <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.25 }}>Track *</Box>
              <Select
                value={selectedTrack}
                onChange={handleMultiSelectChange(setSelectedTrack)}
                multiple
                disabled={loadingTracks}
                displayEmpty
                input={<OutlinedInput />}
                variant="outlined"
                size="small"
                MenuProps={dropdownMenuProps}
                sx={{
                  width: '100%',
                  height: '32px',
                  fontSize: '12px',
                  '& .MuiSelect-select': {
                    py: 0.5,
                    fontSize: '12px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d0d0d0',
                  },
                }}
                renderValue={(selected) => {
                  const values = selected as string[];
                  if (!values.length) return loadingTracks ? 'Loading...' : 'Select Track';
                  const labels = Object.fromEntries(tracks.map((track) => [track.track_id, track.track_name]));
                  return renderSelectedChips(values, labels, (value) =>
                    setSelectedTrack(selectedTrack.filter((item) => item !== value))
                  );
                }}
              >
                {tracks.map((track) => (
                  <MenuItem key={track.track_id} value={track.track_id} sx={{ fontSize: '12px' }}>
                    <Checkbox size="small" checked={selectedTrack.indexOf(track.track_id) > -1} />
                    <ListItemText primary={track.track_name} />
                  </MenuItem>
                ))}
              </Select>
            </Box>
            
            <Box sx={{ minWidth: '180px' }}>
              <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.25 }}>Course *</Box>
              <Select
                value={selectedCourse}
                onChange={handleMultiSelectChange(setSelectedCourse)}
                multiple
                disabled={loadingCourses || selectedTrack.length === 0}
                displayEmpty
                input={<OutlinedInput />}
                variant="outlined"
                size="small"
                MenuProps={dropdownMenuProps}
                sx={{
                  width: '100%',
                  height: '32px',
                  fontSize: '12px',
                  '& .MuiSelect-select': {
                    py: 0.5,
                    fontSize: '12px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d0d0d0',
                  },
                }}
                renderValue={(selected) => {
                  const values = selected as string[];
                  if (!values.length) {
                    return loadingCourses ? 'Loading...' : selectedTrack.length === 0 ? 'Select Track First' : 'Select Course';
                  }
                  const labels = Object.fromEntries(courses.map((course) => [course.course_id, course.course_name]));
                  return renderSelectedChips(values, labels, (value) =>
                    setSelectedCourse(selectedCourse.filter((item) => item !== value))
                  );
                }}
              >
                {courses.map((course) => (
                  <MenuItem key={course.course_id} value={course.course_id} sx={{ fontSize: '12px' }}>
                    <Checkbox size="small" checked={selectedCourse.indexOf(course.course_id) > -1} />
                    <ListItemText primary={course.course_name} />
                  </MenuItem>
                ))}
              </Select>
            </Box>
            
            <Box sx={{ minWidth: '180px' }}>
              <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.25 }}>Batch</Box>
              <Select
                value={selectedBatch}
                onChange={handleMultiSelectChange(setSelectedBatch)}
                multiple
                disabled={loadingBatches || selectedCourse.length === 0}
                displayEmpty
                input={<OutlinedInput />}
                variant="outlined"
                size="small"
                MenuProps={dropdownMenuProps}
                sx={{
                  width: '100%',
                  height: '32px',
                  fontSize: '12px',
                  '& .MuiSelect-select': {
                    py: 0.5,
                    fontSize: '12px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d0d0d0',
                  },
                }}
                renderValue={(selected) => {
                  const values = selected as string[];
                  if (!values.length) {
                    return loadingBatches ? 'Loading...' : selectedCourse.length === 0 ? 'Select Course First' : 'Select Batch';
                  }
                  const labels = Object.fromEntries(batches.map((batch) => [batch.batch_id, batch.batch_name]));
                  return renderSelectedChips(values, labels, (value) =>
                    setSelectedBatch(selectedBatch.filter((item) => item !== value))
                  );
                }}
              >
                {batches.map((batch) => (
                  <MenuItem key={batch.batch_id} value={batch.batch_id} sx={{ fontSize: '12px' }}>
                    <Checkbox size="small" checked={selectedBatch.indexOf(batch.batch_id) > -1} />
                    <ListItemText primary={batch.batch_name} />
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ minWidth: '140px' }}>
              <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.25 }}>Subject</Box>
              <Select
                value={selectedSubject}
                onChange={handleMultiSelectChange(setSelectedSubject)}
                multiple
                disabled={loadingSubjects || selectedCourse.length === 0 || selectedBatch.length === 0}
                displayEmpty
                input={<OutlinedInput />}
                variant="outlined"
                size="small"
                MenuProps={dropdownMenuProps}
                sx={{
                  width: '100%',
                  height: '32px',
                  fontSize: '12px',
                  '& .MuiSelect-select': {
                    py: 0.5,
                    fontSize: '12px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d0d0d0',
                  },
                }}
                renderValue={(selected) => {
                  const values = selected as string[];
                  if (!values.length) {
                    if (loadingSubjects) return 'Loading...';
                    if (selectedCourse.length === 0 || selectedBatch.length === 0) return 'Select Course/Batch';
                    return 'Select Subject';
                  }
                  const labels = Object.fromEntries(subjects.map((subject) => [subject, subject]));
                  return renderSelectedChips(values, labels, (value) =>
                    setSelectedSubject(selectedSubject.filter((item) => item !== value))
                  );
                }}
              >
                {subjects.map((subject) => (
                  <MenuItem key={subject} value={subject} sx={{ fontSize: '12px' }}>
                    <Checkbox size="small" checked={selectedSubject.indexOf(subject) > -1} />
                    <ListItemText primary={subject} />
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ minWidth: '140px' }}>
              <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.25 }}>Project</Box>
              <Select
                value={selectedProject}
                onChange={handleMultiSelectChange(setSelectedProject)}
                multiple
                disabled={loadingProjects || selectedCourse.length === 0 || selectedBatch.length === 0}
                displayEmpty
                input={<OutlinedInput />}
                variant="outlined"
                size="small"
                MenuProps={dropdownMenuProps}
                sx={{
                  width: '100%',
                  height: '32px',
                  fontSize: '12px',
                  '& .MuiSelect-select': {
                    py: 0.5,
                    fontSize: '12px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d0d0d0',
                  },
                }}
                renderValue={(selected) => {
                  const values = selected as string[];
                  if (!values.length) {
                    return loadingProjects
                      ? 'Loading...'
                      : selectedCourse.length === 0 || selectedBatch.length === 0
                        ? 'Select Course/Batch First'
                        : 'Select Project';
                  }
                  const labels = Object.fromEntries(
                    projects.map((project) => [
                      String(project.batch_project_id),
                      project.batch_project_name || project.project_name || '',
                    ])
                  );
                  return renderSelectedChips(values, labels, (value) =>
                    setSelectedProject(selectedProject.filter((item) => item !== value))
                  , 2);
                }}
              >
                {projects.map((project) => {
                  const value = String(project.batch_project_id);
                  return (
                    <MenuItem key={value} value={value} sx={{ fontSize: '12px' }}>
                      <Checkbox size="small" checked={selectedProject.indexOf(value) > -1} />
                      <ListItemText primary={project.batch_project_name || project.project_name || ''} />
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
          </Box>

          {/* MUI Filter Row */}
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'space-between', p: '0.5rem' }}>
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <MRT_GlobalFilterTextField table={table} />
            <MRT_ToggleFiltersButton table={table} />
            <Typography variant="body2" color="textSecondary">
                  Selected: {selectedRows.length} total ({table.getSelectedRowModel().flatRows.length} from current view)
            </Typography>
          </Box>
          <Button
              color="primary"
              disabled={selectedRows.length === 0 || session_id === 0}
            onClick={handleLogSelected}
            variant="contained"
              size="small"
          >
            Send Invite {session_id === 0 ? '(No Session ID)' : ''}
          </Button>
          </Box>
        </Box>
      );
    },
  });

  if (isAdding) {
    return (
      <div className="my-2 me-2 rounded-2 bg-white" style={{ height: `calc(100vh - 90px)`,display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <PulseLoader color="#1976d2" />
      </div>
    );
  }

  return (
    <div className="my-2 me-2 rounded-2" style={{ backgroundColor: "white", height: `calc(100vh - 95px)`,overflowY: "auto" }}>
    <MediaQuery minWidth={1137}>
    <div>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MaterialReactTable table={table} />
      </LocalizationProvider>
    </div>
    </MediaQuery>

      {/* Mobile View */}
    <MediaQuery maxWidth={1136}>
        <Box sx={{ p: 1, backgroundColor: '#fff', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)', borderRadius: 1 }}>
          {/* Mobile Filters */}
           <Box sx={{ p: 1.25, borderBottom: '1px solid #eee' }}>
             <Typography variant="h6" sx={{ mb: 1.25, fontSize: '16px', fontWeight: 'medium' }}>Filters</Typography>
             <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
               <Box>
                 <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'medium' }}>Track *</Typography>
                 <Select
                   value={selectedTrack}
                   onChange={handleMultiSelectChange(setSelectedTrack)}
                   multiple
                   disabled={loadingTracks}
                   displayEmpty
                   input={<OutlinedInput />}
                   variant="outlined"
                   size="small"
                   MenuProps={dropdownMenuProps}
                   sx={{
                     width: '100%',
                     height: '32px',
                     fontSize: '12px',
                     '& .MuiSelect-select': {
                       py: 0.5,
                       fontSize: '12px',
                     },
                     '& .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#d0d0d0',
                     },
                   }}
                   renderValue={(selected) => {
                     const values = selected as string[];
                     if (!values.length) return loadingTracks ? 'Loading...' : 'Select Track';
                     const labels = Object.fromEntries(tracks.map((track) => [track.track_id, track.track_name]));
                     return renderSelectedChips(values, labels, (value) =>
                       setSelectedTrack(selectedTrack.filter((item) => item !== value))
                     );
                   }}
                 >
                   {tracks.map((track) => (
                     <MenuItem key={track.track_id} value={track.track_id} sx={{ fontSize: '12px' }}>
                       <Checkbox size="small" checked={selectedTrack.indexOf(track.track_id) > -1} />
                       <ListItemText primary={track.track_name} />
                     </MenuItem>
                   ))}
                 </Select>
               </Box>
               
               <Box>
                 <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'medium' }}>Course *</Typography>
                 <Select
                   value={selectedCourse}
                   onChange={handleMultiSelectChange(setSelectedCourse)}
                   multiple
                   disabled={loadingCourses || selectedTrack.length === 0}
                   displayEmpty
                   input={<OutlinedInput />}
                   variant="outlined"
                   size="small"
                   MenuProps={dropdownMenuProps}
                   sx={{
                     width: '100%',
                     height: '32px',
                     fontSize: '12px',
                     '& .MuiSelect-select': {
                       py: 0.5,
                       fontSize: '12px',
                     },
                     '& .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#d0d0d0',
                     },
                   }}
                   renderValue={(selected) => {
                     const values = selected as string[];
                     if (!values.length) {
                       return loadingCourses ? 'Loading...' : selectedTrack.length === 0 ? 'Select Track First' : 'Select Course';
                     }
                     const labels = Object.fromEntries(courses.map((course) => [course.course_id, course.course_name]));
                     return renderSelectedChips(values, labels, (value) =>
                       setSelectedCourse(selectedCourse.filter((item) => item !== value))
                     );
                   }}
                 >
                   {courses.map((course) => (
                     <MenuItem key={course.course_id} value={course.course_id} sx={{ fontSize: '12px' }}>
                       <Checkbox size="small" checked={selectedCourse.indexOf(course.course_id) > -1} />
                       <ListItemText primary={course.course_name} />
                     </MenuItem>
                   ))}
                 </Select>
               </Box>
               
               <Box>
                 <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'medium' }}>Batch</Typography>
                 <Select
                   value={selectedBatch}
                   onChange={handleMultiSelectChange(setSelectedBatch)}
                   multiple
                   disabled={loadingBatches || selectedCourse.length === 0}
                   displayEmpty
                   input={<OutlinedInput />}
                   variant="outlined"
                   size="small"
                   MenuProps={dropdownMenuProps}
                   sx={{
                     width: '100%',
                     height: '32px',
                     fontSize: '12px',
                     '& .MuiSelect-select': {
                       py: 0.5,
                       fontSize: '12px',
                     },
                     '& .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#d0d0d0',
                     },
                   }}
                   renderValue={(selected) => {
                     const values = selected as string[];
                     if (!values.length) {
                       return loadingBatches ? 'Loading...' : selectedCourse.length === 0 ? 'Select Course First' : 'Select Batch';
                     }
                     const labels = Object.fromEntries(batches.map((batch) => [batch.batch_id, batch.batch_name]));
                     return renderSelectedChips(values, labels, (value) =>
                       setSelectedBatch(selectedBatch.filter((item) => item !== value))
                     );
                   }}
                 >
                   {batches.map((batch) => (
                     <MenuItem key={batch.batch_id} value={batch.batch_id} sx={{ fontSize: '12px' }}>
                       <Checkbox size="small" checked={selectedBatch.indexOf(batch.batch_id) > -1} />
                       <ListItemText primary={batch.batch_name} />
                     </MenuItem>
                   ))}
                 </Select>
               </Box>
               <Box>
                 <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'medium' }}>Subject</Typography>
                 <Select
                   value={selectedSubject}
                   onChange={handleMultiSelectChange(setSelectedSubject)}
                   multiple
                   disabled={loadingSubjects || selectedCourse.length === 0 || selectedBatch.length === 0}
                   displayEmpty
                   input={<OutlinedInput />}
                   variant="outlined"
                   size="small"
                   MenuProps={dropdownMenuProps}
                   sx={{
                     width: '100%',
                     height: '32px',
                     fontSize: '12px',
                     '& .MuiSelect-select': {
                       py: 0.5,
                       fontSize: '12px',
                     },
                     '& .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#d0d0d0',
                     },
                   }}
                   renderValue={(selected) => {
                     const values = selected as string[];
                     if (!values.length) {
                       if (loadingSubjects) return 'Loading...';
                       if (selectedCourse.length === 0 || selectedBatch.length === 0) return 'Select Course/Batch';
                       return 'Select Subject';
                     }
                     const labels = Object.fromEntries(subjects.map((subject) => [subject, subject]));
                     return renderSelectedChips(values, labels, (value) =>
                       setSelectedSubject(selectedSubject.filter((item) => item !== value))
                     );
                   }}
                 >
                   {subjects.map((subject) => (
                     <MenuItem key={subject} value={subject} sx={{ fontSize: '12px' }}>
                       <Checkbox size="small" checked={selectedSubject.indexOf(subject) > -1} />
                       <ListItemText primary={subject} />
                     </MenuItem>
                   ))}
                 </Select>
               </Box>
               <Box>
                 <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'medium' }}>Project</Typography>
                 <Select
                   value={selectedProject}
                   onChange={handleMultiSelectChange(setSelectedProject)}
                   multiple
                  disabled={loadingProjects || selectedCourse.length === 0 || selectedBatch.length === 0}
                   displayEmpty
                   input={<OutlinedInput />}
                   variant="outlined"
                   size="small"
                   MenuProps={dropdownMenuProps}
                   sx={{
                     width: '100%',
                     height: '32px',
                     fontSize: '12px',
                     '& .MuiSelect-select': {
                       py: 0.5,
                       fontSize: '12px',
                     },
                     '& .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#d0d0d0',
                     },
                   }}
                   renderValue={(selected) => {
                     const values = selected as string[];
                     if (!values.length) {
                      return loadingProjects
                        ? 'Loading...'
                        : selectedCourse.length === 0 || selectedBatch.length === 0
                          ? 'Select Course/Batch First'
                          : 'Select Project';
                     }
                     const labels = Object.fromEntries(
                      projects.map((project) => [
                        String(project.batch_project_id),
                        project.batch_project_name || project.project_name || '',
                      ])
                     );
                    return renderSelectedChips(values, labels, (value) =>
                       setSelectedProject(selectedProject.filter((item) => item !== value))
                    , 2);
                   }}
                 >
                   {projects.map((project) => {
                    const value = String(project.batch_project_id);
                     return (
                       <MenuItem key={value} value={value} sx={{ fontSize: '12px' }}>
                         <Checkbox size="small" checked={selectedProject.indexOf(value) > -1} />
                        <ListItemText primary={project.batch_project_name || project.project_name || ''} />
                       </MenuItem>
                     );
                   })}
                 </Select>
               </Box>
             </Box>
           </Box>

   <div style={{height:'95px',maxHeight:'95px'}}>
   <div>
              <h5>Students</h5>
   </div>

   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',}}>
     <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }} className='pb-1 border-1 border-bottom border-black'>
       <button
         disabled={selectedRows.length === 0}
         onClick={handleLogSelected}
         style={{ backgroundColor: '#61b0f9', color: 'black', }}
         className='p-1 m-0'
       >
         Send Invite to Selected Students
       </button>
       <p>
         Selected: {selectedRows.length} total ({selectedRows.filter(id => students.some(s => s.stuId === id)).length} from current view)
       </p>
     </Box>
   </Box>
   </div>
   
          <div style={{ overflowX: 'auto', overflowY: 'auto',height: 'calc(100vh - 350px)'}}>
     <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
       <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f1f1f1', zIndex: 10 }}>
         <tr>
           <th>
             <input
               type="checkbox"
               checked={selectAll}
               onChange={handleSelectAllChange}
               title={selectAll ? "Deselect all current students" : "Select all current students"}
             />
           </th>
           <th>ID</th>
           <th>Name</th>
           <th>College</th>
           <th>Branch</th>
           <th>Phone</th>
           <th>Email</th>
         </tr>
       </thead>
       <tbody>
         {students.map((student) => (
           <tr key={student.stuId}>
             <td>
               <input
                 type="checkbox"
                 checked={selectedRows.includes(student.stuId)}
                 onChange={() => handleSelectRowChange(student.stuId)}
               />
             </td>
             <td>{student.stuId}</td>
             <td>{student.stuname}</td>
             <td>{student.collegeName}</td>
             <td>{student.branch}</td>
             <td>{student.phonenumber}</td>
             <td>{student.email}</td>
           </tr>
         ))}
       </tbody>
     </table>
   </div>
 </Box>
</MediaQuery>
<Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          const currentPath = window.location.pathname;
          const isAdminRoute = currentPath.startsWith('/admin');
          const hasAdminPermission = sessionStorage.getItem("Admin");
          
          if (isAdminRoute && !hasAdminPermission) {
            navigate('/trainer/view-sessions');
            return;
          }
          
          const prefix = (isAdminRoute && hasAdminPermission) ? "/admin" : "/trainer";
          navigate(getViewSessionsPath(prefix));
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {successfulMessage}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => {
            setShowModal(false);
            const currentPath = window.location.pathname;
            const isAdminRoute = currentPath.startsWith('/admin');
            const hasAdminPermission = sessionStorage.getItem("Admin");
            
            if (isAdminRoute && !hasAdminPermission) {
              navigate('/trainer/view-sessions');
              return;
            }
            
            const prefix = (isAdminRoute && hasAdminPermission) ? "/admin" : "/trainer";
            navigate(getViewSessionsPath(prefix));
          }}>
            Close
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StudentSelecTable;
